import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { cn } from "@/lib/utils";

const SESSION_KEY = "novifood_assistant_session";

function getSessionId() {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
        id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem(SESSION_KEY, id);
    }
    return id;
}

export default function AssistantWidget() {
    const { tr } = useLocale();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const greeting = tr(
        "Olá! Sou o assistente NOVIFOOD. Conte-me sobre o seu negócio (café, gelados, eventos…) e recomendo o carrinho ideal.",
        "Hi! I'm the NOVIFOOD assistant. Tell me about your business (coffee, ice cream, events…) and I'll recommend the ideal cart."
    );

    async function send(e) {
        e?.preventDefault();
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const res = await fetch(`${API}/assistant/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: getSessionId(), message: text }),
            });
            if (!res.ok || !res.body) throw new Error("stream failed");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    try {
                        const ev = JSON.parse(part.slice(6));
                        if (ev.delta) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.delta };
                                return copy;
                            });
                        } else if (ev.error) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: ev.error };
                                return copy;
                            });
                        }
                    } catch { /* skip malformed */ }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content: tr("Não consegui responder agora. Tente novamente ou fale connosco em /contacto.", "I couldn't reply right now. Try again or reach us at /contacto."),
                };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    }

    return (
        <>
            {open && (
                <div
                    data-testid="assistant-panel"
                    className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-border bg-white shadow-[0_32px_64px_-16px_rgba(11,37,69,0.35)] overflow-hidden flex flex-col"
                    style={{ height: "min(560px, calc(100vh - 160px))" }}
                >
                    <div className="gradient-navy text-white px-5 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary">
                                <Bot className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="font-heading font-bold leading-tight">{tr("Assistente NOVIFOOD", "NOVIFOOD Assistant")}</p>
                                <p className="text-[11px] text-white/70 flex items-center gap-1">
                                    <Sparkles className="h-3 w-3 text-secondary" /> {tr("Recomendações com IA", "AI-powered recommendations")}
                                </p>
                            </div>
                        </div>
                        <button type="button" onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Fechar" className="text-white/70 hover:text-white">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/40">
                        <Bubble role="assistant">{greeting}</Bubble>
                        {messages.map((m, i) => (
                            <Bubble key={i} role={m.role}>
                                {m.content || (streaming && i === messages.length - 1 ? <TypingDots /> : "")}
                            </Bubble>
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    <form onSubmit={send} className="border-t border-border bg-white p-3 flex items-center gap-2">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={tr("Ex.: quero vender gelados em Faro…", "E.g.: I want to sell ice cream…")}
                            data-testid="assistant-input"
                            className="flex-1 rounded-full border border-border bg-muted px-4 py-2.5 text-sm text-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <button
                            type="submit"
                            disabled={streaming || !input.trim()}
                            data-testid="assistant-send"
                            aria-label={tr("Enviar", "Send")}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white disabled:opacity-50 hover:-translate-y-0.5 transition"
                        >
                            <Send className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="assistant-fab"
                aria-label={tr("Assistente IA", "AI Assistant")}
                className={cn(
                    "fixed bottom-24 right-4 sm:right-6 z-[59] inline-flex h-14 w-14 items-center justify-center rounded-full shadow-[0_16px_32px_-8px_rgba(11,37,69,0.55)] transition hover:-translate-y-1",
                    open
                        ? "opacity-0 pointer-events-none"
                        : "bg-primary text-secondary ring-4 ring-secondary/40 ring-offset-2 ring-offset-white/70 hover:ring-secondary/70"
                )}
            >
                <Bot className="h-7 w-7" />
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-secondary border-2 border-white animate-pulse" />
            </button>
        </>
    );
}

function Bubble({ role, children }) {
    const isUser = role === "user";
    return (
        <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
            <div
                className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                    isUser ? "bg-primary text-white rounded-br-md" : "bg-white border border-border text-primary rounded-bl-md"
                )}
            >
                {typeof children === "string" ? renderLite(children) : children}
            </div>
        </div>
    );
}

/* Minimal markdown: **bold** and internal /links */
function renderLite(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|\/(?:simulador|contacto|modelo-alfa|modelo-omega|processos|faq|eventos|sobre)\b)/g);
    return parts.map((p, i) => {
        if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (/^\//.test(p)) return <a key={i} href={p} className="font-semibold underline text-primary">{p}</a>;
        return p;
    });
}

function TypingDots() {
    return (
        <span className="inline-flex gap-1 items-center py-1">
            {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
            ))}
        </span>
    );
}
